import { AnalyticsApi, MockAnalyticsApi } from '../analytics'
import {
  Host,
  InitializationContext,
  InitializationOptions,
  SharedStorageHostApi,
} from '../Host.ts'
import { AdsApi, MockAdsApi } from '../ads'
import { createMockStorageApi, StorageApi } from '../storage'
import { FirestoreStorageApi } from '../storage/FirestoreStorageApi'
import { ValidatingStorageApi } from '../storage/ValidatingStorageApi'
import { Avatar3dApi, MockAvatarApi } from '../avatar3d'
import { RundotGameAPI } from '../rundot-game-api/types.ts'
import { MockNavigationApi, NavigationApi } from '../navigation'
import { MockNotificationsApi, NotificationsApi } from '../notifications'
import { MockPopupsApi, PopupsApi } from '../popups'
import { ProfileApi, PlaygroundProfileApi } from '../profile'
import { MockSystemApi, SystemApi } from '../system'
import { MockDeviceApi } from '../device'
import { MockEnvironmentApi } from '../environment'
import { CdnApi, MockCdnApi } from '../cdn'
import { MockTimeApi, TimeApi } from '../time'
import { AiApi } from '../ai'
import { HttpAiApi } from '../ai/HttpAiApi'
import type { TextGenApi } from '../textGen/TextGenApi'
import { HapticsApi, MockHapticsApi } from '../haptics'
import { GamepadApi, MockGamepadApi } from '../gamepad'
import { FeaturesApi, MockFeaturesApi } from '../features'
import { LiveOpsApi } from '../liveops'
import { HttpLiveOpsApi } from '../liveops/HttpLiveOpsApi'
import { LifecycleApi, MockLifecycleApi } from '../lifecycles'
import { SimulationApi } from '../simulation'
import { HttpSimulationApi } from '../simulation/HttpSimulationApi'
import { initializeRoomsApi, RoomsApi } from '../rooms'
import { HttpRoomsApi } from '../rooms/HttpRoomsApi'
import { FirestoreRoomsApi } from '../rooms/FirestoreRoomsApi'
import { CompositeRemoteRoomsApi } from '../rooms/CompositeRemoteRoomsApi'
import { LoggingApi, MockLoggingApi } from '../logging'
import { IapApi } from '../iap'
import { HttpIapApi } from '../iap/HttpIapApi'
import { CreditsApi } from '../credits'
import { HttpCreditsApi } from '../credits/HttpCreditsApi'
import { startHeartbeat, onGoogleSignedOut, setPlayerId } from './tabCoordinator'
import { stripForcedPlayerParamFromUrl } from './forcedPlayerIdentity'
import { RundotGameMessageId } from '../RundotGameMessageId.ts'
import {
  MockActionSheetItem,
  MockActionSheetOptions,
  MockOverlay,
} from '../MockOverlay'
import { LeaderboardApi } from '../leaderboard'
import { UgcApi } from '../ugc'
import { HttpLeaderboardApi } from '../leaderboard/HttpLeaderboardApi'
import { callCloudRunFunction } from '../http/callRemoteFunction'
import { getPlaygroundGameSessionToken } from '../http/playgroundGameSession'
import type { BackendHttpDeps } from '../http/backendHttpDeps'
import { HttpUgcApi } from '../ugc/HttpUgcApi'
import { MockPreloaderApi, PreloaderApi } from '../game-preloader'
import { SocialApi } from '../social/SocialApi'
import { HttpSocialApi } from '../social/HttpSocialApi'
import { getCloudRunUrl, getPlaygroundConfig, getPlaygroundRoomServerUrl } from '../config/playground'
import { createHostedMultiplayerApi, resolveHostedMultiplayerTarget } from './hostedMultiplayer'
import {
  exchangeForCustomToken,
  type FirebaseClient,
  getFirebaseClient,
  signInWithGoogle,
} from '../firebase/firebaseClient'
import { PlaygroundAuthStateMachine } from './PlaygroundAuthStateMachine'
import { expectBootReload, flushBootTelemetry, markBootPhase, timeBootPhase } from './BootTelemetry'
import { ImageGenApi } from '../imageGen/ImageGenApi'
import { HttpImageGenApi } from '../imageGen/HttpImageGenApi'
import { AudioGenApi } from '../audioGen/AudioGenApi'
import { HttpAudioGenApi } from '../audioGen/HttpAudioGenApi'
import { VideoGenApi } from '../videoGen/VideoGenApi'
import { HttpVideoGenApi } from '../videoGen/HttpVideoGenApi'
import { FilesApi } from '../files/FilesApi'
import { HttpFilesApi } from '../files/HttpFilesApi'
import { ClipsApi } from '../clips/ClipsApi'
import { ClipsApiImpl } from '../clips/ClipsApiImpl'
import { BrowserCaptureConsent } from '../clips/BrowserCaptureConsent'
import { SpriteGenApi } from '../spriteGen/SpriteGenApi'
import { HttpSpriteGenApi } from '../spriteGen/HttpSpriteGenApi'
import { ThreeDGenApi } from '../threeDGen/ThreeDGenApi'
import { HttpThreeDGenApi } from '../threeDGen/HttpThreeDGenApi'
import { EntitlementApi } from '../entitlements'
import { HttpEntitlementApi } from '../entitlements/HttpEntitlementApi'
import { ChoicesApi } from '../choices'
import { HttpChoicesApi } from '../choices/HttpChoicesApi'
import { StatsApi, MockStatsApi } from '../stats'
import { CollectiblesApi, MockCollectiblesApi } from '../collectibles'
import { ShopApi } from '../shop'
import { HttpShopApi } from '../shop/HttpShopApi'
import {
  AccessGateApi,
  applyAccessGates,
  MockAccessGateApi,
} from '../access-gate'
import { MockMultiplayerApi, MultiplayerApi } from '../mp/client'
import { MockVideoApi, VideoApi } from '../video'
import { ActivityApi, MockActivityApi } from '../activity'
import { AppApi } from '../app/AppApi'
import { PlaygroundAppApi } from '../app/PlaygroundAppApi'
import { AssetLibraryApi, MockAssetLibraryApi } from '../assetLibrary'
import { AttributionApi, MockAttributionApi } from '../attribution'
import { buildAutoProvisionProfileBody } from './username.ts'

enum PlaygroundHostState {
  PLAYING,
  PAUSED,
  SLEEPING,
  TERMINATED,
}

/**
 * PlaygroundHost connects to a real backend (local emulator, dev, or staging)
 * for browser-based development with live data.
 */
export class PlaygroundHost implements Host {
  readonly ads: AdsApi
  readonly analytics: AnalyticsApi
  readonly deviceCache: StorageApi
  readonly appStorage: StorageApi
  readonly ownerStorage: StorageApi
  readonly sharedStorage: SharedStorageHostApi
  readonly avatar3d: Avatar3dApi
  readonly navigation: NavigationApi
  readonly notifications: NotificationsApi
  readonly popups: PopupsApi
  readonly profile: ProfileApi
  readonly system: SystemApi
  readonly cdn: CdnApi
  readonly time: TimeApi
  ai: AiApi
  textGen: TextGenApi
  readonly haptics: HapticsApi
  readonly gamepad: GamepadApi
  readonly features: FeaturesApi
  readonly liveops: LiveOpsApi
  readonly lifecycle: LifecycleApi
  simulation: SimulationApi
  rooms: RoomsApi
  readonly logging: LoggingApi
  readonly iap: IapApi
  readonly credits: CreditsApi
  readonly leaderboard: LeaderboardApi
  ugc: UgcApi
  readonly preloader: PreloaderApi
  readonly social: SocialApi
  imageGen: ImageGenApi
  audioGen: AudioGenApi
  videoGen: VideoGenApi
  files: FilesApi
  clips: ClipsApi
  spriteGen: SpriteGenApi
  threeDGen: ThreeDGenApi
  readonly entitlements: EntitlementApi
  readonly choices: ChoicesApi
  readonly stats: StatsApi
  readonly collectibles: CollectiblesApi
  readonly shop: ShopApi
  readonly accessGate: AccessGateApi
  readonly multiplayer: MultiplayerApi
  readonly video: VideoApi
  readonly activity: ActivityApi
  readonly app: AppApi
  readonly attribution: AttributionApi
  readonly assetLibrary: AssetLibraryApi

  context?: InitializationContext
  readonly instanceId: string = 'playground'

  private state: PlaygroundHostState = PlaygroundHostState.PLAYING

  get isInitialized() {
    return this._isInitialized
  }

  private readonly rundotGameApi: RundotGameAPI
  private _isInitialized: boolean = false
  private _cachedBackendProfile: {
    uid: string
    profile: {
      id?: string
      username?: string
      name?: string
      avatarUrl?: string | null
      userId?: string
    }
  } | null = null
  private readonly _overlay: MockOverlay

  private readonly _mockLifecyclesApi: MockLifecycleApi
  private readonly _mockAdsApi: MockAdsApi
  private client: FirebaseClient | null = null
  private clientReady: Promise<FirebaseClient> | null = null
  private stateMachine: PlaygroundAuthStateMachine | null = null
  private readonly cleanupFunctions: Array<() => void> = []
  private _stopHeartbeat: (() => void) | null = null
  private _stopSignOutListener: (() => void) | null = null
  private _configCountdownTimer: ReturnType<typeof setInterval> | null = null
  private _configCountdownSeconds: number = 0

  constructor(rundotGameApi: RundotGameAPI) {
    this.rundotGameApi = rundotGameApi
    this._overlay = this.createOverlay()
    this._mockAdsApi = new MockAdsApi(this._overlay)
    this._mockLifecyclesApi = new MockLifecycleApi()

    this.ads = this._mockAdsApi
    this.analytics = new MockAnalyticsApi()

    // Get playground configuration (guaranteed to exist when PlaygroundHost is created)
    const playgroundConfig = getPlaygroundConfig()!
    const gameId = playgroundConfig.gameId

    // profileId is fetched from Firebase Auth (set after user signs in)
    const getProfileId = (): string => {
      if (this.client && this.client.isSignedIn()) {
        return this.client.getProfileId() || 'unknown-user'
      }
      return 'unknown-user'
    }

    // Use Firestore-backed storage APIs (fall back to mock when gameId is missing,
    // e.g. playground before first game creation — autoCreatePlaygroundGame will
    // set the gameId and reload the page)
    this.deviceCache = createMockStorageApi('deviceCache', '') // deviceCache stays local
    this.appStorage = gameId
      ? new ValidatingStorageApi(new FirestoreStorageApi(gameId))
      : createMockStorageApi('appStorage', '')
    // ownerStorage in playground uses a local mock: the PlaygroundHost goes direct-to-Firestore
    // for appStorage, but owner resolution (gameId → ownerUserId) is only implemented in
    // the Cloud Run backend. Until the playground grows an HttpStorageApi, keep ownerStorage
    // isolated per-tab via local storage.
    this.ownerStorage = createMockStorageApi('ownerStorage', gameId || '')

    // Playground sharedStorage: no Firestore-backed implementation yet — use
    // namespaced in-memory mocks. Playground cannot enforce the target-owned
    // access policy without hitting Cloud Run, so access-control tests should
    // run against a real dev backend via RemoteHost.
    const ownGameId = gameId
    const resolveSelfAppId = (op: 'open' | 'read', appId?: string): string => {
      if (appId === '') {
        throw new Error(
          `sharedStorage.${op}: appId must be a non-empty string or omitted`,
        )
      }
      if (appId !== undefined) return appId
      if (!ownGameId) {
        throw new Error(
          `sharedStorage.${op}: cannot resolve self appId — playground gameId is empty`,
        )
      }
      return ownGameId
    }
    this.sharedStorage = {
      open: ({ appId, namespace }) => {
        const resolved = resolveSelfAppId('open', appId)
        return createMockStorageApi(
          'appStorage',
          `sandbox#shared/${resolved}/${namespace}`,
        )
      },
      read: ({ appId }) => {
        resolveSelfAppId('read', appId)
        return {
          listSources: async () => [],
          getAllFromSource: async () => ({}),
          get: async () => null,
          getAllForKey: async () => [],
        }
      },
    }

    // Use HTTP-backed simulation API with Firestore subscriptions
    this.simulation = new HttpSimulationApi(gameId, {
      getProfileId,
      versionTag: playgroundConfig.versionTag,
      callRemote: callCloudRunFunction,
    })

    // LiveOps reads go through the same Cloud Run H5 proxy (GST attached).
    // Default to the `private` tag — the tag the HMR upload repoints.
    // Exposures go to MockAnalyticsApi (console-only): playground/dev assignment
    // must never reach the warehouse. Verify variants via getConfigAsync().assignments.
    this.liveops = new HttpLiveOpsApi(gameId, {
      callRemote: callCloudRunFunction,
      versionTag: playgroundConfig.versionTag ?? 'private',
      hooks: {
        getUnitId: () => this.rundotGameApi._profileData?.id,
        onExposure: (assignment, configVersion) => {
          // Fire-and-forget: swallow async rejection (see RemoteHost for rationale).
          void this.analytics
            .recordCustomEvent('liveops_experiment_exposure', {
              experiment_id: assignment.experimentId,
              variant_id: assignment.variantId,
              variant_weight: assignment.variantWeight,
              total_weight: assignment.totalWeight,
              config_version: configVersion,
            })
            .catch(() => {})
        },
      },
    })

    // Use HTTP-backed leaderboard API (callRemote is now required)
    this.leaderboard = new HttpLeaderboardApi(gameId, { callRemote: callCloudRunFunction })
    this.ugc = new HttpUgcApi(gameId, { versionTag: playgroundConfig.versionTag })

    // Use composite rooms API (HTTP for lifecycle, Firestore for data)
    const httpRoomsApi = new HttpRoomsApi(gameId, { getProfileId })
    const firestoreRoomsApi = new FirestoreRoomsApi()
    this.rooms = new CompositeRemoteRoomsApi(httpRoomsApi, firestoreRoomsApi)

    // Shared mock APIs (UI/system - same behavior as MockHost)
    this.avatar3d = new MockAvatarApi(rundotGameApi)
    this.navigation = new MockNavigationApi(rundotGameApi)
    this.notifications = new MockNotificationsApi(rundotGameApi)
    this.popups = new MockPopupsApi(this._overlay)
    this.profile = new PlaygroundProfileApi(rundotGameApi)

    const deviceApi = new MockDeviceApi(rundotGameApi)
    const environmentApi = new MockEnvironmentApi(rundotGameApi)
    this.system = new MockSystemApi(deviceApi, environmentApi, rundotGameApi)

    this.cdn = new MockCdnApi(rundotGameApi)
    this.time = new MockTimeApi(rundotGameApi)
    this.ai = new HttpAiApi(gameId)
    this.textGen = this.ai
    this.haptics = new MockHapticsApi(rundotGameApi)
    this.gamepad = new MockGamepadApi()
    this.features = new MockFeaturesApi()
    this.lifecycle = this._mockLifecyclesApi
    this.logging = new MockLoggingApi()
    const playgroundIap = new HttpIapApi(gameId)
    this.iap = playgroundIap
    this.social = new HttpSocialApi(gameId)
    // The generation Http*Api are firebase-free; supply the playground's auth + URL.
    const playgroundHttpDeps: BackendHttpDeps = {
      getIdToken: async () => (await getFirebaseClient()).getIdTokenOrThrow(),
      getCloudRunUrl,
      getGameSessionToken: getPlaygroundGameSessionToken,
    }
    this.credits = new HttpCreditsApi(gameId, playgroundHttpDeps)
    this.imageGen = new HttpImageGenApi(gameId, playgroundHttpDeps)
    this.audioGen = new HttpAudioGenApi(gameId, playgroundHttpDeps)
    this.videoGen = new HttpVideoGenApi(gameId, playgroundHttpDeps)
    this.files = new HttpFilesApi(gameId)
    this.clips = new ClipsApiImpl({
      files: this.files,
      ugc: this.ugc,
      captureConsent: new BrowserCaptureConsent(),
    })
    this.spriteGen = new HttpSpriteGenApi(gameId, playgroundHttpDeps)
    this.threeDGen = new HttpThreeDGenApi(gameId, playgroundHttpDeps)
    this.app = new PlaygroundAppApi(gameId, playgroundConfig.appRole, playgroundConfig.target !== 'local')
    // Real shop + entitlements against the backend, resolving the same tag the
    // playground's config upload repoints (mirrors HttpLiveOpsApi's default).
    this.entitlements = new HttpEntitlementApi(gameId, playgroundHttpDeps)
    this.choices = new HttpChoicesApi(playgroundHttpDeps)
    this.stats = new MockStatsApi()
    this.collectibles = new MockCollectiblesApi()
    this.shop = new HttpShopApi(gameId, playgroundHttpDeps, {
      versionTag: playgroundConfig.versionTag ?? 'private',
      directPurchaseCheckout: (sku, orderId) => (
        playgroundIap.purchaseDirectSku(sku, orderId)
      ),
      directPurchaseCheckoutCompleted: (orderId) => {
        playgroundIap.completeDirectSku(orderId)
      },
    })
    this.video = new MockVideoApi()
    this.activity = new MockActivityApi()
    // R9(b): when a hosted room-server URL is injected (playground target only),
    // connect real Syncplay/GameRoom clients to it with authenticated tickets;
    // otherwise fall back to the local dev sidecar / offline mock.
    // Hosted MP requires both an injected room-server URL AND a version tag.
    // The tag is exactly `config.versionTag` (`private` once server config exists,
    // which any deterministic/GameRoom game has) — the SAME value
    // getPlaygroundGameSessionToken mints the GST under (it now mints under
    // 'private' unconditionally, since developing tags skip the server's
    // version-existence check), so the ticket's room
    // version and the GST `ver` claim are provably identical, including under
    // enforce. Without server config there is no room config to host anyway, so
    // fall back to the local sidecar / offline mock.
    const hostedTarget = resolveHostedMultiplayerTarget(
      getPlaygroundRoomServerUrl(),
      playgroundConfig.versionTag,
    )
    this.multiplayer = hostedTarget
      ? createHostedMultiplayerApi({
          roomServerUrl: hostedTarget.roomServerUrl,
          gameId,
          versionTag: hostedTarget.versionTag,
          getIdToken: async () => (await getFirebaseClient()).getIdTokenOrThrow(),
          getGst: getPlaygroundGameSessionToken,
        })
      : new MockMultiplayerApi({
          getProfile: () => rundotGameApi.getProfile(),
        })
    this.attribution = new MockAttributionApi()
    this.assetLibrary = new MockAssetLibraryApi(rundotGameApi)

    initializeRoomsApi(this.rundotGameApi, this)

    this.preloader = new MockPreloaderApi()
    rundotGameApi.isMock = () => true
    this.rundotGameApi.sharedAssets = this.assetLibrary

    this.accessGate = new MockAccessGateApi(rundotGameApi)
    applyAccessGates(this)

    // Set up Firebase auth and sign-in for toolbar
    this.setupPlaygroundSignIn()
  }

  /**
   * Set up auth state machine, global sign-in/sign-out functions, and config listeners.
   */
  private setupPlaygroundSignIn(): void {
    if (typeof window === 'undefined') return

    // Create the central auth state machine
    this.stateMachine = new PlaygroundAuthStateMachine({
      onCreateProfile: async (user) => {
        this._cachedBackendProfile = null
        // Set a placeholder profile immediately with correct id (uid).
        this.rundotGameApi._profileData = {
          id: user.uid,
          username: user.isAnonymous
            ? `Player ${user.uid.slice(-4).toUpperCase()}`
            : user.displayName ||
              user.email?.split('@')[0] ||
              `Dev User ${user.uid.slice(-8)}`,
          name: user.isAnonymous ? undefined : user.displayName || undefined,
          avatarUrl: user.isAnonymous ? null : user.photoURL || null,
          isAnonymous: user.isAnonymous,
        }
        // Ensure a backend profile exists (idempotent).
        await timeBootPhase('profile-ensure', () => this.ensureProfileExists(user))
      },
      onFetchProfile: async (user) => {
        // Fetch backend profile and upgrade _profileData with canonical fields.
        await timeBootPhase('profile-fetch', async () => {
          try {
            const cached =
              this._cachedBackendProfile?.uid === user.uid
                ? this._cachedBackendProfile.profile
                : null
            this._cachedBackendProfile = null
            const backendProfile =
              cached ??
              (await this.callCloudRunProfile<{
                id?: string
                username?: string
                name?: string
                avatarUrl?: string | null
                userId?: string
              } | null>('GET', '/v1/profile/'))

            if (backendProfile?.id && backendProfile?.username) {
              this.rundotGameApi._profileData = {
                id: backendProfile.id,
                username: backendProfile.username,
                name: backendProfile.name || undefined,
                avatarUrl: backendProfile.avatarUrl ?? null,
                isAnonymous: user.isAnonymous,
              }
            }
          } catch (error) {
            console.warn(
              '[RUN:playground] Failed to fetch backend profile:',
              error,
            )
            // Boot telemetry: the error is swallowed by design (boot proceeds
            // with Firebase-only profile), so mark the failure explicitly —
            // timeBootPhase would otherwise record a success.
            markBootPhase('profile-fetch:error')
          }
        })
      },
      onNeedsGameCreation: async (user) => {
        const config = getPlaygroundConfig()
        if (config) {
          // NOTE: ends in a full page reload on success — BootTelemetry
          // persists marks to sessionStorage on every mark, so the timings
          // recorded so far survive the reload and merge into the next load.
          // Timed inside autoCreatePlaygroundGame itself (NOT timeBootPhase):
          // it swallows failures via setError, and setError synchronously
          // starts the error flush — a wrapper would record the error mark
          // only after the flush payload was already built.
          await this.autoCreatePlaygroundGame(user, config)
        }
      },
      onNoUser: () => {
        this.rundotGameApi._profileData = undefined
      },
    })

    // Flush boot telemetry on terminal states. Fire-and-forget — never
    // awaited, never blocks boot.
    this.stateMachine.subscribe((state) => {
      if (state.phase === 'ready') {
        flushBootTelemetry('ready')
      } else if (state.phase === 'error') {
        flushBootTelemetry('error', state.message)
      }
    })

    // Start the state machine — it handles Firebase init and auth listening
    this.clientReady = this.stateMachine.start().then((client) => {
      this.client = client

      // Store tab coordinator info from Firebase init
      const tabInfo =
        typeof window !== 'undefined'
          ? window.__RUNDOT_GAME_PLAYGROUND_TAB_INFO__
          : undefined
      if (tabInfo) {
        // Start heartbeat for tab presence and listen for cross-tab sign-out
        this._stopHeartbeat = startHeartbeat(tabInfo.tabId)
        this._stopSignOutListener = onGoogleSignedOut(() => {
          window.location.reload()
        })
      }

      return client
    })

    this.clientReady.catch((error) => {
      console.error(
        '[RUN:playground] Firebase client init failed:',
        error,
      )
    })

    // Expose Google Sign-In function for toolbar (dev/staging only).
    window.__RUNDOT_GAME_PLAYGROUND_SIGN_IN_WITH_GOOGLE__ =
      async (): Promise<void> => {
        await signInWithGoogle()
      }

    // Expose global player switching function for toolbar.
    // For playground: updates the coordinator and reloads (re-enters initializeFirebaseClient).
    // For dev/staging: re-authenticates with a synthetic UID derived from the real user + playerId.
    window.__RUNDOT_GAME_PLAYGROUND_SWITCH_PLAYER__ = async (
      playerId?: string,
    ): Promise<void> => {
      const playgroundConfig = getPlaygroundConfig()

      if (playgroundConfig?.target === 'playground') {
        // Playground: write new playerId to coordinator, then reload.
        // Strip a URL-forced identity (?rundotPlayer=) first — it survives the
        // reload and would silently re-apply the old player over this switch.
        stripForcedPlayerParamFromUrl()
        const tabInfo = window.__RUNDOT_GAME_PLAYGROUND_TAB_INFO__
        if (tabInfo?.tabId) {
          setPlayerId(
            tabInfo.tabId,
            playerId === undefined ? null : playerId,
          )
        }
        window.location.reload()
        return
      }

      // Dev/staging: exchange token and sign in directly
      const client = await getFirebaseClient()
      const user = client.getCurrentUser()
      if (!user) {
        throw new Error(
          '[RUN] Player switching requires an active sign-in session.',
        )
      }
      const idToken = await user.getIdToken(true)
      const cloudRunBase = playgroundConfig?.cloudRunUrl || '/__rundotcloudrun'
      const customToken = await exchangeForCustomToken(
        idToken,
        cloudRunBase,
        playerId,
      )

      const { signInWithCustomToken } = await import('firebase/auth')
      await signInWithCustomToken(client.auth, customToken)
      window.location.reload()
    }

    // Expose global local sign-in-as function for toolbar button (local emulator mode only)
    window.__RUNDOT_GAME_PLAYGROUND_SIGN_IN_AS__ = async (
      playerId: string,
    ): Promise<void> => {
      if (this.client) {
        try {
          await this.client.signInWithLocalPlayer(playerId)
        } catch (error) {
          console.error('[RUN] Local sign-in failed:', error)
          throw error
        }
        return
      }

      if (!this.clientReady) {
        throw new Error('[RUN] Firebase not configured.')
      }

      console.warn(
        '[RUN] Firebase client still initializing, please wait a moment...',
      )
      throw new Error(
        '[RUN] Firebase client still initializing. Please wait a moment and try again.',
      )
    }

    // Expose global sign-out function for toolbar button.
    // Sign-out triggers the state machine's onAuthStateChanged(null) path,
    // which handles registry cleanup and page reload for all targets.
    window.__RUNDOT_GAME_PLAYGROUND_SIGN_OUT__ = async () => {
      try {
        const client = await getFirebaseClient()
        await client.signOut()
      } catch (error) {
        console.error('[RUN] Sign-out failed:', error)
        throw error
      }
    }

    // Register beforeunload handler for playground tab cleanup.
    const playgroundConfigForCleanup = getPlaygroundConfig()
    if (playgroundConfigForCleanup?.target === 'playground') {
      const beforeUnloadHandler = () => {
        if (this._stopHeartbeat) {
          this._stopHeartbeat()
          this._stopHeartbeat = null
        }
      }
      window.addEventListener('beforeunload', beforeUnloadHandler)
      this.cleanupFunctions.push(() =>
        window.removeEventListener('beforeunload', beforeUnloadHandler),
      )
    }

    // Listen for server config file changes bridged from HMR via the virtual playground-init module.
    const configChangedHandler = () => {
      // Reset countdown to 7 seconds (debounce — restarts on every change)
      this._configCountdownSeconds = 7
      window.dispatchEvent(
        new CustomEvent('rundot-game-config-countdown', {
          detail: { secondsRemaining: this._configCountdownSeconds },
        }),
      )

      // If a timer is already ticking, the reset above is sufficient
      if (this._configCountdownTimer) return

      this._configCountdownTimer = setInterval(() => {
        this._configCountdownSeconds--

        if (this._configCountdownSeconds > 0) {
          window.dispatchEvent(
            new CustomEvent('rundot-game-config-countdown', {
              detail: { secondsRemaining: this._configCountdownSeconds },
            }),
          )
          return
        }

        // Countdown reached zero — clear timer and upload
        clearInterval(this._configCountdownTimer!)
        this._configCountdownTimer = null
        this._executeConfigUpload()
      }, 1000)
    }
    window.addEventListener(
      'rundot-hmr-server-config-changed',
      configChangedHandler,
    )
    this.cleanupFunctions.push(() => {
      window.removeEventListener(
        'rundot-hmr-server-config-changed',
        configChangedHandler,
      )
      if (this._configCountdownTimer) {
        clearInterval(this._configCountdownTimer)
        this._configCountdownTimer = null
      }
    })

    // Allow users to cancel the config upload countdown
    const configCancelHandler = () => {
      if (this._configCountdownTimer) {
        clearInterval(this._configCountdownTimer)
        this._configCountdownTimer = null
      }
      window.dispatchEvent(new CustomEvent('rundot-game-config-uploaded'))
    }
    window.addEventListener(
      'rundot-game-config-countdown-cancel',
      configCancelHandler,
    )
    this.cleanupFunctions.push(() =>
      window.removeEventListener(
        'rundot-game-config-countdown-cancel',
        configCancelHandler,
      ),
    )

    // Allow users to skip the countdown and upload immediately
    const configSkipHandler = () => {
      // Only skip if a countdown is actually in progress
      if (!this._configCountdownTimer) return
      clearInterval(this._configCountdownTimer)
      this._configCountdownTimer = null
      this._executeConfigUpload()
    }
    window.addEventListener(
      'rundot-game-config-countdown-skip',
      configSkipHandler,
    )
    this.cleanupFunctions.push(() =>
      window.removeEventListener(
        'rundot-game-config-countdown-skip',
        configSkipHandler,
      ),
    )

    // Track global functions and state machine for cleanup
    this.cleanupFunctions.push(() => {
      this.stateMachine?.destroy()
      delete window.__RUNDOT_GAME_PLAYGROUND_SWITCH_PLAYER__
      delete window.__RUNDOT_GAME_PLAYGROUND_SIGN_IN_AS__
      delete window.__RUNDOT_GAME_PLAYGROUND_SIGN_OUT__
      delete window.__RUNDOT_GAME_PLAYGROUND_SIGN_IN_WITH_GOOGLE__
      delete window.__RUNDOT_GAME_PLAYGROUND_TAB_INFO__
    })
  }

  /**
   * Make an authenticated request to Cloud Run profile endpoints.
   * Profile endpoints live on Cloud Run (not Firebase Functions), so they must
   * use the Cloud Run proxy (/__rundotcloudrun) instead of callRemoteFunction.
   */
  private async callCloudRunProfile<TResponse>(
    method: string,
    endpoint: string,
    body?: unknown,
  ): Promise<TResponse> {
    const client = await getFirebaseClient()
    const idToken = await client.getIdTokenOrThrow()
    const baseUrl = getCloudRunUrl()
    const url = `${baseUrl}${endpoint}`

    const options: RequestInit = {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
    }

    if (body !== undefined) {
      options.body = JSON.stringify(body)
    }

    const response = await fetch(url, options)

    if (!response.ok) {
      const text = await response.text()
      throw new Error(
        `[RUN] Cloud Run call ${method} ${endpoint} failed (${response.status}): ${text}`,
      )
    }

    const text = await response.text()
    if (!text) return null as TResponse
    return JSON.parse(text) as TResponse
  }

  /**
   * Execute the config upload flow (shared by countdown-reached-zero and skip).
   */
  private _executeConfigUpload(): void {
    window.dispatchEvent(new CustomEvent('rundot-game-config-uploading'))

    const currentConfig = getPlaygroundConfig()
    if (
      !currentConfig?.hasServerConfig ||
      !currentConfig.gameId ||
      !currentConfig.gameManagerUrl
    ) {
      console.warn(
        '[RUN:playground] Skipping re-upload — missing config fields',
        {
          hasServerConfig: currentConfig?.hasServerConfig,
          gameId: currentConfig?.gameId,
          gameManagerUrl: currentConfig?.gameManagerUrl,
        },
      )
      window.dispatchEvent(new CustomEvent('rundot-game-config-uploaded'))
      return
    }

    // Re-upload config and reload to pick up new values
    getFirebaseClient()
      .then((client) => {
        const user = client.getCurrentUser()
        if (!user) {
          console.warn(
            '[RUN:playground] Skipping re-upload — no authenticated user',
          )
          window.dispatchEvent(new CustomEvent('rundot-game-config-uploaded'))
          return
        }
        this.uploadServerConfig(user, currentConfig)
          .then((uploaded) => {
            if (!uploaded) {
              // Config unchanged — no upload happened, no reload needed
              window.dispatchEvent(
                new CustomEvent('rundot-game-config-uploaded'),
              )
              return
            }
            // Clear cached simulation config so next call fetches fresh data
            if (this.simulation && 'clearConfigCache' in this.simulation) {
              ;(this.simulation as HttpSimulationApi).clearConfigCache()
            }
            // Clear cached liveops rules so an edit to rundot/liveops.config.json
            // shows up on the next getConfigAsync() (mirrors simulation clear).
            if (this.liveops && 'clearConfigCache' in this.liveops) {
              ;(this.liveops as HttpLiveOpsApi).clearConfigCache()
            }
            // The reload below rebuilds the host (and this cache); the explicit
            // clear only covers a catalog read racing the reload.
            if (this.shop && 'clearCatalogCache' in this.shop) {
              ;(this.shop as HttpShopApi).clearCatalogCache()
            }
            // Reload to pick up the newly uploaded config
            window.dispatchEvent(
              new CustomEvent('rundot-game-config-countdown', {
                detail: {
                  secondsRemaining: 0,
                  message: 'Reloading with new config\u2026',
                },
              }),
            )
            window.location.reload()
          })
          .catch((err) => {
            console.warn(
              '[RUN:playground] Config re-upload failed:',
              err,
            )
            window.dispatchEvent(
              new CustomEvent('rundot-game-config-upload-error', {
                detail: { message: err?.message || String(err) },
              }),
            )
          })
      })
      .catch(() => {
        window.dispatchEvent(new CustomEvent('rundot-game-config-uploaded'))
      })
  }

  /**
   * Ensure a profile exists for the signed-in user.
   * Checks if profile exists first to avoid overwriting existing profiles on staging/dev.
   * Only creates a new profile if one doesn't exist.
   *
   * Profile endpoints are on Cloud Run (not Firebase Functions):
   *   GET  /v1/profile/       -> getProfile
   *   POST /v1/profile/create -> createUserProfile
   */
  private async ensureProfileExists(
    user: import('firebase/auth').User,
  ): Promise<void> {
    try {
      // First, check if profile already exists
      try {
        const existingProfile = await this.callCloudRunProfile<{
          id?: string
          username?: string
          name?: string
          avatarUrl?: string | null
          userId?: string
        } | null>('GET', '/v1/profile/')
        if (existingProfile?.id) {
          this._cachedBackendProfile = { uid: user.uid, profile: existingProfile }
          return
        }
      } catch (checkError: unknown) {
        // 404 or "not found" means no profile - continue to create
        // Other errors we'll log but still try to create
        const err = checkError as { message?: string; status?: number }
        if (!err?.message?.includes('not found') && err?.status !== 404) {
          console.warn(
            '[RUN] Error checking profile, will attempt creation:',
            err?.message,
          )
        }
      }

      const isAnonymous = user.isAnonymous === true

      // Send NO username: client-derived display strings (Google displayName,
      // synthetic sandbox handles) violate the server's username charset/length
      // gate. Omitting it routes the server into its guaranteed-valid, unique
      // `anonymous_<userId>` auto-provision path.
      await this.callCloudRunProfile(
        'POST',
        '/v1/profile/create',
        buildAutoProvisionProfileBody({
          description: isAnonymous
            ? 'Auto-created anonymous playground user'
            : 'Auto-created for playground development',
          birthdate: '2000-01-01',
          onboardingCompleted: true,
        }),
      )
    } catch (error: unknown) {
      // Profile creation failed - log but don't throw
      const message = error instanceof Error ? error.message : String(error)
      console.warn('[RUN] Could not ensure profile exists:', message)
    }
  }

  /**
   * Auto-create a game via the Game Manager API (playground only).
   * After creation, writes the config file via the Vite middleware and reloads.
   */
  private async autoCreatePlaygroundGame(
    user: import('firebase/auth').User,
    playgroundConfig: import('../config/playground').PlaygroundConfig,
  ): Promise<void> {
    // Boot telemetry: timed here (not via timeBootPhase at the call site) —
    // see onNeedsGameCreation for why. Date.now matches BootTelemetry's clock.
    const createStart = Date.now()
    try {
      const idToken = await user.getIdToken()
      const gameManagerUrl = playgroundConfig.gameManagerUrl!

      // Call the Game Manager to create a new game
      const createResponse = await fetch(
        `${gameManagerUrl}/v1/games/createGame`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            name: playgroundConfig.suggestedGameName ?? 'Playground Game',
            description: 'Auto-created playground game for local development',
          }),
        },
      )

      if (!createResponse.ok) {
        const text = await createResponse.text()
        throw new Error(
          `Game creation failed (${createResponse.status}): ${text}`,
        )
      }

      const createResponseBody = await createResponse.json()
      const responseData = createResponseBody as {
        data?: { id?: string }
        id?: string
      }
      const newGameId = responseData.data?.id || responseData.id
      if (!newGameId) {
        throw new Error(
          `Game Manager returned empty game ID. Full response: ${JSON.stringify(createResponseBody)}`,
        )
      }

      // Persist the gameId to the config file via Vite dev server middleware
      const writeResponse = await fetch('/__rundot-write-game-config', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Rundot-Playground': '1' },
        body: JSON.stringify({ gameId: newGameId, target: 'playground' }),
      })

      if (!writeResponse.ok) {
        console.warn(
          '[RUN:playground] Failed to write game config file, but game was created',
        )
      }

      // Update the playground config in-place so any code that reads it before reload sees the ID
      if (window.__RUNDOT_GAME_PLAYGROUND__) {
        window.__RUNDOT_GAME_PLAYGROUND__.gameId = newGameId
      }

      // Reload so the Vite plugin picks up the new config file
      sessionStorage.setItem('rundot-playground-game-created', '1')
      // Boot telemetry: pin the navigation point explicitly. (location.reload
      // does NOT halt the JS turn — the success mark below still runs and
      // records the real createGame+config-write duration; this mark is what
      // anchors the reload in the phase timeline.)
      markBootPhase('game-create:reload')
      // Boot telemetry: this reload is deliberate — merge (don't abandon) the buffer.
      expectBootReload()
      window.location.reload()
      // Success — recorded AFTER reload() because navigation doesn't stop this
      // JS turn. Persisted to sessionStorage, so it survives and merges.
      markBootPhase('game-create', Date.now() - createStart)
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      console.error(
        '[RUN:playground] Failed to auto-create game:',
        message,
      )
      // Boot telemetry: the error is swallowed here (setError, no rethrow).
      // Record the failure WITH its duration BEFORE setError — setError
      // synchronously starts the error flush, and the attempt's duration is
      // exactly the signal needed when game creation is the slow/failing phase.
      markBootPhase('game-create:error', Date.now() - createStart)

      // Transition state machine to error so toolbar can display it and initialize() unblocks
      if (this.stateMachine) {
        this.stateMachine.setError(message)
      }
    }
  }

  /**
   * Upload merged server config to VGM and link it via the "private" tag.
   * This enables simulation APIs to work in playground by ensuring the game
   * has a server config in Firestore.
   */
  private async uploadServerConfig(
    user: import('firebase/auth').User,
    playgroundConfig: import('../config/playground').PlaygroundConfig,
  ): Promise<boolean> {
    try {
      // Fetch merged config from Vite middleware
      const configResponse = await fetch('/__rundot-server-config', {
        headers: { 'X-Rundot-Playground': '1' },
      })

      if (configResponse.status === 404) {
        // No config files found — skip silently
        markBootPhase('server-config-upload:skipped')
        return false
      }

      if (!configResponse.ok) {
        const body = await configResponse.text()
        console.error(
          '[RUN:playground] Server config merge error:',
          body,
        )
        markBootPhase('server-config-upload:failed')
        return false
      }

      const configPayload = (await configResponse.json()) as {
        config: Record<string, unknown>
        hash: string
        changed: boolean
      }
      const mergedConfig = configPayload.config
      const configHash = configPayload.hash

      if (!mergedConfig || Object.keys(mergedConfig).length === 0) {
        markBootPhase('server-config-upload:skipped')
        return false
      }

      // Skip upload if config hasn't changed since last successful upload
      if (!configPayload.changed) {
        markBootPhase('server-config-upload:skipped')
        return false
      }

      const idToken = await user.getIdToken()
      const gameManagerUrl = playgroundConfig.gameManagerUrl
      if (!gameManagerUrl) {
        console.warn(
          '[RUN:playground] No Game Manager URL configured, cannot upload server config',
        )
        markBootPhase('server-config-upload:skipped')
        return false
      }

      const gameId = playgroundConfig.gameId
      if (!gameId) {
        markBootPhase('server-config-upload:skipped')
        return false
      }

      // Upload server config to VGM
      const uploadResponse = await fetch(
        `${gameManagerUrl}/v1/games/${gameId}/uploadServer`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({ value: mergedConfig }),
        },
      )

      if (!uploadResponse.ok) {
        const text = await uploadResponse.text()
        console.warn(
          `[RUN:playground] Server config upload failed (${uploadResponse.status}): ${text}`,
        )
        markBootPhase('server-config-upload:failed')
        return false
      }

      const uploadBody = (await uploadResponse.json()) as {
        data?: { id?: string }
        id?: string
      }
      const serverId = uploadBody.data?.id || uploadBody.id
      if (!serverId) {
        console.warn(
          '[RUN:playground] Server config upload returned no serverId',
        )
        markBootPhase('server-config-upload:failed')
        return false
      }

      // Link the server config to the "private" tag
      const tagResponse = await fetch(
        `${gameManagerUrl}/v1/games/${gameId}/tags`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({ tag: 'private', serverId, name: 'private' }),
        },
      )

      if (!tagResponse.ok) {
        const text = await tagResponse.text()
        console.warn(
          `[RUN:playground] Tag update failed (${tagResponse.status}): ${text}`,
        )
        markBootPhase('server-config-upload:failed')
        return false
      }

      // Persist the hash so subsequent loads can skip redundant uploads
      try {
        await fetch('/__rundot-server-config-hash', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Rundot-Playground': '1' },
          body: JSON.stringify({ hash: configHash }),
        })
      } catch {
        console.warn(
          '[RUN:playground] Failed to persist config hash (non-critical)',
        )
      }

      return true
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      console.warn(
        '[RUN:playground] Server config upload error:',
        message,
      )
      markBootPhase('server-config-upload:failed')
      return false
    }
  }

  onNotificationParamsUpdate(
    _callback: (params: Record<string, string>) => void,
  ): () => void {
    return () => {}
  }

  async initialize(
    _options?: InitializationOptions,
  ): Promise<InitializationContext> {
    this._isInitialized = true

    // Wait for Firebase auth + profile creation to finish before letting the app proceed.
    if (this.stateMachine) {
      await timeBootPhase('wait-for-profile', () => this.stateMachine!.waitForProfile())
    }

    // Populate system data on RundotGameAPI instance
    // Only set profile if auth callback hasn't already populated it (profileReady guarantees this).
    if (!this.rundotGameApi._profileData) {
      this.rundotGameApi._profileData = this.profile.getCurrentProfile()
    }
    this.rundotGameApi._deviceData = this.system.getDevice()
    this.rundotGameApi._environmentData = this.system.getEnvironment()

    this.rundotGameApi._localeData = this.rundotGameApi._mock?.locale || 'en-US'
    this.rundotGameApi._languageCodeData =
      this.rundotGameApi._mock?.languageCode || 'en'

    this.context = {
      initializeAsleep: false,
      safeArea: this.rundotGameApi._safeAreaData,
      launchParams: _options?.launchParams || {
        PLAYGROUND_LAUNCH_PARAMS_KEY: 'PLAYGROUND_LAUNCH_PARAMS_VALUE',
      },
      shareParams: _options?.shareParams || {
        PLAYGROUND_SHARE_PARAMS_KEY: 'PLAYGROUND_SHARE_PARAMS_VALUE',
      },
      shareLinkId: _options?.shareParams ? 'sandbox-share-id' : undefined,
    }

    // Upload server config before hiding the overlay so simulation APIs work immediately.
    const initPlaygroundConfig = getPlaygroundConfig()
    if (
      initPlaygroundConfig?.hasServerConfig &&
      initPlaygroundConfig.gameId &&
      initPlaygroundConfig.gameManagerUrl
    ) {
      try {
        const client = await getFirebaseClient()
        const user = client.getCurrentUser()
        if (user) {
          await timeBootPhase('server-config-upload', () =>
            this.uploadServerConfig(
              user,
              initPlaygroundConfig,
            ),
          )
        }
      } catch (err) {
        console.warn(
          '[RUN:playground] Initial config upload failed (proceeding anyway):',
          err,
        )
      }
    } else {
      markBootPhase('server-config-upload:skipped')
    }

    // Signal that the playground is fully ready for gameplay
    markBootPhase('game-ready')
    if (this.stateMachine) {
      this.stateMachine.markGameReady()
    }

    return this.context
  }

  // ============================================================
  // Overlay and UI methods (shared with MockHost)
  // ============================================================

  private createOverlay(): MockOverlay {
    const overlayContainer = document.createElement('div')
    overlayContainer.id = 'rundot-game-sandbox-overlay'
    overlayContainer.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 10000;
    `
    document.body.appendChild(overlayContainer)

    // const menuButton = this.createOverlayButton(
    //   'close',
    //   'Menu',
    //   { x: window.innerWidth - 48, y: 16, width: 32, height: 32 },
    //   () => {
    //     this.handleMenuButtonClicked()
    //   },
    //   'rgba(59, 130, 246, 0.7)',
    //   '#FFFFFF',
    // )
    // overlayContainer.appendChild(menuButton)

    const adOverlay = this.setupAdOverlay()
    const actionSheet = this.setupActionSheetOverlay()

    const resizeHandler = (): void => {
      this.updateOverlayLayout()
    }
    window.addEventListener('resize', resizeHandler)
    this.cleanupFunctions.push(() =>
      window.removeEventListener('resize', resizeHandler),
    )

    return {
      container: overlayContainer,
      elements: {},
      appVisibilityState: 'visible',
      actionSheetOverlay: actionSheet,
      adOverlay: adOverlay,
      showAdOverlay: (adType) => {
        return this.showAdOverlay(adType)
      },
      showActionSheet: (items, options) => {
        return this.showActionSheetOverlay(items, options)
      },
    }
  }

  private async handleMenuButtonClicked() {
    if (this.state === PlaygroundHostState.PLAYING) {
      this.triggerLifecycleEvent(RundotGameMessageId.PAUSE)
      this.state = PlaygroundHostState.PAUSED
    }

    const actionSheetItems: MockActionSheetItem[] = []

    const awakeAction: MockActionSheetItem = {
      label: '⏰ Awake',
      id: 'awakeAction',
    }
    const sleepAction: MockActionSheetItem = {
      label: '💤 Sleep',
      id: 'sleepAction',
    }
    const quitAction: MockActionSheetItem = {
      label: '🛑 Quit',
      id: 'quitAction',
    }
    const playAction: MockActionSheetItem = {
      label: '▶️ Play',
      id: 'playAction',
    }

    if (this.state === PlaygroundHostState.PAUSED) {
      actionSheetItems.push(sleepAction)
    } else if (this.state === PlaygroundHostState.SLEEPING) {
      actionSheetItems.push(awakeAction)
    }

    if (this.state !== PlaygroundHostState.TERMINATED) {
      actionSheetItems.push(quitAction)
    } else if (this.state === PlaygroundHostState.TERMINATED) {
      actionSheetItems.push(playAction)
    }

    const action = await this.showActionSheetOverlay(actionSheetItems)

    if (action === awakeAction.id) {
      this.tryAwake()
    } else if (action === sleepAction.id) {
      this.trySleep()
    } else if (action === playAction.id) {
      this.tryPlay()
    } else if (action === quitAction.id) {
      this.tryQuit()
    } else {
      this.tryResume()
    }
  }

  private tryAwake() {
    if (this.state === PlaygroundHostState.SLEEPING) {
      this.triggerLifecycleEvent(RundotGameMessageId.AWAKE)
      this.state = PlaygroundHostState.PAUSED
    }
  }

  private trySleep() {
    if (this.state === PlaygroundHostState.PAUSED) {
      this.triggerLifecycleEvent(RundotGameMessageId.SLEEP)
      this.state = PlaygroundHostState.SLEEPING
    }
  }

  private tryPlay() {
    if (this.state === PlaygroundHostState.TERMINATED) {
      this.triggerLifecycleEvent(RundotGameMessageId.AWAKE)
      this.state = PlaygroundHostState.PAUSED
      this.triggerLifecycleEvent(RundotGameMessageId.RESUME)
      this.state = PlaygroundHostState.PLAYING
    }
  }

  private tryQuit() {
    if (this.state === PlaygroundHostState.PLAYING) {
      this.triggerLifecycleEvent(RundotGameMessageId.PAUSE)
      this.state = PlaygroundHostState.PAUSED
    }

    if (this.state === PlaygroundHostState.PAUSED) {
      this.triggerLifecycleEvent(RundotGameMessageId.SLEEP)
      this.state = PlaygroundHostState.SLEEPING
    }

    if (this.state === PlaygroundHostState.SLEEPING) {
      this.triggerLifecycleEvent(RundotGameMessageId.QUIT)
      this.state = PlaygroundHostState.TERMINATED
    }
  }

  private tryResume() {
    if (this.state === PlaygroundHostState.PAUSED) {
      this.triggerLifecycleEvent(RundotGameMessageId.RESUME)
      this.state = PlaygroundHostState.PLAYING
    }
  }

  private async showAdOverlay(
    type: 'interstitial' | 'rewarded',
  ): Promise<boolean> {
    return new Promise((resolve) => {
      const overlay = this._overlay
      const adOverlay = overlay.adOverlay

      overlay.adOverlay.innerHTML = ''

      const heading = document.createElement('h1')
      heading.style.cssText = `
        font-size: 24px;
        margin-bottom: 20px;
        text-align: center;
      `
      heading.innerText =
        type === 'interstitial' ? 'INTERSTITIAL AD' : 'REWARDED VIDEO AD'
      overlay.adOverlay.appendChild(heading)

      const content = document.createElement('div')
      content.style.cssText = `
        width: 300px;
        height: 250px;
        background-color: #1e40af;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 20px;
        border-radius: 8px;
      `
      content.innerText = 'Mock Ad Content'
      adOverlay.appendChild(content)

      const timer = document.createElement('p')
      timer.style.cssText = `
        margin-bottom: 20px;
        font-size: 16px;
      `
      timer.innerText = 'Normally ads would have a timer...'
      adOverlay.appendChild(timer)

      const okButton = document.createElement('button')
      okButton.style.cssText = `
        padding: 10px 40px;
        background-color: #2563eb;
        color: white;
        border: none;
        border-radius: 4px;
        font-size: 16px;
        cursor: pointer;
      `
      okButton.innerText = 'Close Ad'
      adOverlay.appendChild(okButton)

      adOverlay.style.display = 'flex'

      okButton.onclick = () => {
        this.hideAdOverlay()
        resolve(true)
      }
    })
  }

  private hideAdOverlay() {
    const overlay = this._overlay
    if (overlay.adOverlay) {
      overlay.adOverlay.style.display = 'none'
    }
  }

  private setupActionSheetOverlay() {
    const actionSheetOverlay = document.createElement('div')
    actionSheetOverlay.id = 'rundot-game-action-sheet-overlay'
    actionSheetOverlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10600;
      font-family: sans-serif;
      display: none;
    `
    document.body.appendChild(actionSheetOverlay)
    return actionSheetOverlay
  }

  private createOverlayButton(
    id: string,
    text: string,
    position: { x: number; y: number; width: number; height: number },
    onClick: () => void,
    background: string,
    color: string,
  ) {
    const button = document.createElement('button')
    button.id = `rundot-game-sandbox-${id}-button`
    button.innerText = text
    button.style.cssText = `
      position: absolute;
      left: ${position.x}px;
      top: ${position.y}px;
      width: ${position.width}px;
      min-width: ${position.width}px;
      height: ${position.height}px;
      background: ${background};
      color: ${color};
      border: none;
      border-radius: 8px;
      font-family: sans-serif;
      font-weight: bold;
      font-size: ${Math.min(position.width, position.height) / 3}px;
      cursor: pointer;
      pointer-events: auto;
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0.9;
      transition: opacity 0.2s;
    `
    button.addEventListener('click', onClick)
    button.addEventListener('mouseover', () => {
      button.style.opacity = '1'
    })
    button.addEventListener('mouseout', () => {
      button.style.opacity = '0.9'
    })

    return button
  }

  private updateOverlayLayout(): void {
    // No-op: playground uses its own toolbar, no overlay needed
  }

  private triggerLifecycleEvent(name: string) {
    if (name === RundotGameMessageId.PAUSE) {
      this._mockLifecyclesApi.triggerPauseCallbacks()
    } else if (name === RundotGameMessageId.RESUME) {
      this._mockLifecyclesApi.triggerResumeCallbacks()
    } else if (name === RundotGameMessageId.QUIT) {
      this._mockLifecyclesApi.triggerQuitCallbacks()
    } else if (name === RundotGameMessageId.AWAKE) {
      this._mockLifecyclesApi.triggerAwakeCallbacks()
    } else if (name === RundotGameMessageId.SLEEP) {
      this._mockLifecyclesApi.triggerSleepCallbacks()
    }
  }

  private setupAdOverlay() {
    const adOverlay = document.createElement('div')
    adOverlay.id = 'rundot-game-ad-overlay'
    adOverlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(37, 99, 235, 0.9);
      color: white;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      z-index: 10500;
      font-family: sans-serif;
      display: none;
    `
    document.body.appendChild(adOverlay)
    return adOverlay
  }

  private showActionSheetOverlay(
    items: MockActionSheetItem[],
    options?: MockActionSheetOptions,
  ): Promise<string | number | null> {
    return new Promise((resolve) => {
      const overlay = this._overlay
      overlay.actionSheetOverlay.innerHTML = ''
      overlay.actionSheetOverlay.style.display = 'flex'

      const actionSheet = document.createElement('div')
      actionSheet.className = 'rundot-game-action-sheet'
      actionSheet.style.cssText = `
        background-color: white;
        border-radius: 8px;
        width: 80%;
        max-width: 400px;
        max-height: 80%;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        color: black;
      `

      if (options?.title) {
        const titleContainer = document.createElement('div')
        titleContainer.style.cssText = `
          padding: 16px;
          border-bottom: 1px solid #eaeaea;
          font-weight: bold;
          font-size: 18px;
          text-align: center;
          color: black;
        `
        titleContainer.innerText = options.title
        actionSheet.appendChild(titleContainer)
      }

      if (options?.message) {
        const messageContainer = document.createElement('div')
        messageContainer.style.cssText = `
          padding: 8px 16px;
          color: #666;
          font-size: 14px;
          text-align: center;
        `
        messageContainer.innerText = options.message
        actionSheet.appendChild(messageContainer)
      }

      const optionsContainer = document.createElement('div')
      optionsContainer.style.cssText = `
        overflow-y: auto;
        max-height: 300px;
      `

      items.forEach((item, index) => {
        const optionItem = document.createElement('div')
        optionItem.style.cssText = `
          padding: 12px 16px;
          border-bottom: 1px solid #eaeaea;
          cursor: pointer;
          display: flex;
          align-items: center;
          transition: background-color 0.2s;
          color: black;
        `

        optionItem.addEventListener('mouseover', () => {
          optionItem.style.backgroundColor = '#f5f5f5'
        })

        optionItem.addEventListener('mouseout', () => {
          optionItem.style.backgroundColor = 'white'
        })

        if (item.icon) {
          const iconSpan = document.createElement('span')
          iconSpan.style.cssText = `
            margin-right: 12px;
            font-size: 16px;
          `
          iconSpan.innerText = item.icon
          optionItem.appendChild(iconSpan)
        }

        const labelSpan = document.createElement('span')
        labelSpan.style.cssText = `color: black;`
        labelSpan.innerText = item.label
        optionItem.appendChild(labelSpan)

        optionItem.addEventListener('click', () => {
          this.hideActionSheetOverlay()
          const optionId = item.id !== undefined ? item.id : index
          resolve(optionId)
        })

        optionsContainer.appendChild(optionItem)
      })

      actionSheet.appendChild(optionsContainer)

      if (!options?.disableCancel) {
        const cancelButton = document.createElement('div')
        cancelButton.style.cssText = `
          padding: 14px 16px;
          text-align: center;
          font-weight: bold;
          cursor: pointer;
          color: #3b82f6;
          border-top: 1px solid #eaeaea;
        `
        cancelButton.innerText = options?.cancelButtonText || 'Cancel'
        cancelButton.addEventListener('click', () => {
          this.hideActionSheetOverlay()
          resolve(null)
        })

        actionSheet.appendChild(cancelButton)
      }

      if (!options?.disableCancel) {
        const closeButton = document.createElement('div')
        closeButton.style.cssText = `
          position: absolute;
          top: 8px;
          right: 8px;
          width: 24px;
          height: 24px;
          border-radius: 12px;
          background-color: rgba(0,0,0,0.1);
          color: #666;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          font-size: 14px;
        `
        closeButton.innerText = '✕'
        closeButton.addEventListener('click', () => {
          this.hideActionSheetOverlay()
          resolve(null)
        })

        actionSheet.appendChild(closeButton)
      }

      // Always append the action sheet to the DOM.
      // Cancel/close affordances are optional, but the sheet itself must render even when
      // `disableCancel` is true (in that case the user must select an option).
      overlay.actionSheetOverlay.appendChild(actionSheet)
    })
  }

  private hideActionSheetOverlay() {
    const overlay = this._overlay
    if (overlay.actionSheetOverlay) {
      overlay.actionSheetOverlay.style.display = 'none'
    }
  }

  /**
   * Clean up all resources held by PlaygroundHost.
   * Call this when the host is no longer needed to prevent memory leaks.
   */
  destroy(): void {
    // Stop heartbeat interval for tab presence
    if (this._stopHeartbeat) {
      this._stopHeartbeat()
      this._stopHeartbeat = null
    }

    // Stop cross-tab sign-out listener
    if (this._stopSignOutListener) {
      this._stopSignOutListener()
      this._stopSignOutListener = null
    }

    // Destroy state machine (unsubscribes from auth)
    if (this.stateMachine) {
      this.stateMachine.destroy()
      this.stateMachine = null
    }

    // Run all cleanup functions (removes event listeners, global functions)
    for (const cleanup of this.cleanupFunctions) {
      try {
        cleanup()
      } catch (e) {
        console.warn('[RUN] Cleanup error:', e)
      }
    }
    this.cleanupFunctions.length = 0

    // Clean up simulation subscriptions
    if (this.simulation && 'destroy' in this.simulation) {
      ;(this.simulation as { destroy(): void }).destroy()
    }

    // Clean up rooms subscriptions
    if (this.rooms && 'destroy' in this.rooms) {
      ;(this.rooms as { destroy(): void }).destroy()
    }

    // Remove DOM elements
    if (typeof document !== 'undefined') {
      const overlay = this._overlay
      if (overlay.container?.parentNode) {
        overlay.container.parentNode.removeChild(overlay.container)
      }
      if (overlay.adOverlay?.parentNode) {
        overlay.adOverlay.parentNode.removeChild(overlay.adOverlay)
      }
      if (overlay.actionSheetOverlay?.parentNode) {
        overlay.actionSheetOverlay.parentNode.removeChild(
          overlay.actionSheetOverlay,
        )
      }
    }

    // Clear references
    this.client = null
    this.clientReady = null
  }
}
